import { action, computed } from 'mobx';
import { Company } from '~/models/company.model';
import { ICompanyResponse, IMember } from '~/types/server.types';
import {
    createFontsStyleTag,
    getFontMarkdown,
    getFontsStyleTag,
    loadGoogleFonts
} from '~/utils/load-font.util';
import { ICompanyFont } from '~/types/model.types';
import { BaseGlobalEntityStore } from '~/stores/base-global-entity.store';
import type { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';

export class CompanyStore extends BaseGlobalEntityStore<
    ICompanyResponse,
    Company
> {
    constructor(globalContext: AuthenticatedGlobalContextValue) {
        super(globalContext, Company);
    }

    @action
    update(item: Partial<ICompanyResponse> & { _id: string }) {
        // Map legacy/underscored keys coming from some API endpoints to the
        // canonical property names used throughout the React codebase.
        const keyAlias: Record<string, string> = {
            brochure_templates: 'brochures',
            brochure_containers: 'old_brochures',
            cover_images: 'images'
        };

        const rawKeys = Object.keys(item);

        const incomingModel = new Company(
            item as ICompanyResponse,
            this.globalContext
        );

        const existingModel = this.all[item._id];

        if (existingModel) {
            rawKeys.forEach(rawKey => {
                const targetKey = keyAlias[rawKey] || rawKey;
                existingModel[targetKey] = (incomingModel as any)[rawKey];
            });

            this.all[item._id] = existingModel;
        } else {
            this.all[item._id] = incomingModel;
        }

        return this.all[item._id];
    }

    @computed
    get currCompany() {
        return this.byId(this.globalContext.sessionStore.currCompanyId);
    }

    @computed
    get hasTeamMembers() {
        return this.teamMemberCount > 0;
    }

    @computed
    get teamMemberCount() {
        return this.currCompany?.team_members?.length ?? 0;
    }

    @computed
    get isEligibleForInstantPayout() {
        return (
            this.currCompany?.eligible_for_instant_payouts &&
            !this.currCompany?.eligible_for_finance_app_payments
        );
    }

    get hasBankAccount() {
        return !!this.currCompany?.bank_account_payment_method_id;
    }

    get isAutomaticInstantPayoutEnabled() {
        return this.currCompany?.all_inclusive_instant_payouts ?? false;
    }

    get isEligibleForFinanceAppPayments() {
        return this.currCompany?.eligible_for_finance_app_payments;
    }

    isTeamMember(member: IMember) {
        return this.currCompany.allCompanyMembers.some(
            companyMember => companyMember._id === member?._id
        );
    }

    loadFontToStyleTag(fontsStyleTag: HTMLStyleElement, font: ICompanyFont) {
        fontsStyleTag.innerHTML += getFontMarkdown(font);
    }

    getPipelineMainViewId() {
        return this.currCompany?.pipelineViews?.find(
            view => view.type === 'main'
        )?._id;
    }

    @action
    loadFonts() {
        const fontsStyleTag = getFontsStyleTag() || createFontsStyleTag();
        this.currCompany?.company_fonts.forEach(companyFont => {
            if (companyFont.url.match(/fonts\.googleapis\.com\//)) {
                loadGoogleFonts(companyFont.name);
            } else {
                this.loadFont(companyFont.url, companyFont.name);
                this.loadFontToStyleTag(fontsStyleTag, companyFont);
            }
        });
    }

    async loadFont(file, fullName) {
        const buffer =
            typeof file === 'string'
                ? `url('${file}')`
                : await file.arrayBuffer();
        const font = new FontFace(fullName, buffer);
        return font.load().then(() => {
            document.fonts.add(font);
        });
    }
}
